
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import type { AdminUser } from '../../types';
import { useToast } from '../../hooks/useToast';

const AdminUserFormPage: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState<'admin' | 'developer'>('admin');
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        if(password.length < 6) {
            showToast('Password should be at least 6 characters long.', { type: 'error' });
            setLoading(false);
            return;
        }
        
        if(role === 'admin' && !username.trim()) {
            showToast('Username is required for admin users.', { type: 'error' });
            setLoading(false);
            return;
        }

        try {
            // Step 1: Check for unique username if provided
            if (username.trim()) {
                const usersRef = collection(db, 'users');
                const q = query(usersRef, where("username", "==", username.trim()));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    showToast('Username is already taken. Please choose another one.', { type: 'error' });
                    setLoading(false);
                    return;
                }
            }
            
            // Step 2: Create user in Firebase Authentication
            const userCredential = await auth.createUserWithEmailAndPassword(email, password);
            const user = userCredential.user;

            if (!user) {
                throw new Error("User creation failed.");
            }

            // Step 3: Create user document in Firestore with their role
            const userDocRef = doc(db, 'users', user.uid);
            const newUser: Omit<AdminUser, 'id'> = {
                email: user.email!,
                role: role,
                username: username.trim() || undefined,
            };
            await setDoc(userDocRef, newUser);
            showToast('User created successfully!', { type: 'success' });
            navigate('/admin/users');

        } catch (err: any) {
            if (err.code === 'auth/email-already-in-use') {
                showToast('This email address is already in use.', { type: 'error' });
            } else if (err.code === 'auth/invalid-email') {
                showToast('Please enter a valid email address.', { type: 'error' });
            } else if (err.code === 'auth/operation-not-allowed') {
                 showToast('Email/Password sign-in is disabled. Please enable it in your Firebase console.', { type: 'error' });
            } else {
                showToast('Failed to create user. Please try again.', { type: 'error' });
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Add New User</h2>
            <div className="bg-white p-8 rounded-lg shadow-md max-w-lg">
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
                        <input 
                            type="email" 
                            name="email" 
                            id="email" 
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required 
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                        />
                         <p className="mt-1 text-xs text-gray-500">Required by the system for account management (e.g., password resets). Developers will use this to log in.</p>
                    </div>
                     <div>
                        <label htmlFor="username" className="block text-sm font-medium text-gray-700">Username {role === 'admin' && <span className="text-red-500">*</span>}</label>
                        <input 
                            type="text" 
                            name="username" 
                            id="username" 
                            value={username}
                            onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
                            required={role === 'admin'}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                        />
                         <p className="mt-1 text-xs text-gray-500">Admins will use this to log in. No spaces allowed.</p>
                    </div>
                     <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                        <input 
                            type="password" 
                            name="password" 
                            id="password" 
                             value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required 
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                        />
                         <p className="mt-1 text-xs text-gray-500">Password must be at least 6 characters long.</p>
                    </div>
                     <div>
                        <label htmlFor="role" className="block text-sm font-medium text-gray-700">Role</label>
                        <select 
                            name="role" 
                            id="role" 
                            value={role}
                            onChange={(e) => setRole(e.target.value as 'admin' | 'developer')}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                        >
                            <option value="admin">Admin</option>
                            <option value="developer">Developer</option>
                        </select>
                         <p className="mt-1 text-xs text-gray-500">Developers can access User Management and Developer Settings.</p>
                    </div>
                     <div className="flex justify-end space-x-4 pt-4">
                        <button type="button" onClick={() => navigate('/admin/users')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400">
                            {loading ? 'Creating...' : 'Create User'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
};

export default AdminUserFormPage;